// =====================================================================
// Certificação — a leitura de FILA dos relatórios.
//
// A tela "Relatórios gravados" se lê como arquivo, mas o que ela guarda é
// trabalho em andamento: em 17/08/2026, 22 dos 24 relatórios estavam abertos e
// só 2 certificados. Estas funções extraem dela as duas perguntas que o gestor
// faz e que o STATUS sozinho não responde:
//
//   1. "Quem está segurando este relatório?"  → aguardandoDe()
//   2. "Há quanto tempo ele está parado?"     → diasParado()
//
// São puras de propósito (entram os campos do relatório, sai texto/número):
// é a única lógica de risco desta tela — tabela e filtro não se testam.
// =====================================================================

// Papéis que validam cada etapa. Os dois assinam; enquanto faltar um, a etapa
// não avança (o diagnóstico é GATE da Etapa 2).
const GERENTE = 'Gerente';
const CONSULTOR = 'Consultor';

// Quem ainda não assinou, na ordem em que os papéis aparecem no relatório.
// Recebe os NOMES gravados (coluna preenchida = assinou).
const faltantes = (nomeGerente, nomeConsultor) => {
  const f = [];
  if (!nomeGerente) f.push(GERENTE);
  if (!nomeConsultor) f.push(CONSULTOR);
  return f;
};

/**
 * Quem o relatório está esperando, em linguagem de quem opera o programa.
 *
 * Devolve `{ texto, papeis, tipo }`:
 *   - `texto`  → o que aparece na coluna ("Validação Etapa 1: Gerente")
 *   - `papeis` → ['Gerente'] — para o gerente reconhecer a própria fila
 *   - `tipo`   → 'validacao' | 'trabalho' | 'nenhum' (governa a cor do chip)
 *
 * ⚠️ 'Em diagnóstico' NÃO significa "esperando validação" sempre: quando os dois
 * papéis já assinaram, o gate caiu e quem está devendo é a Manutenção, que
 * precisa iniciar a Etapa 2. Eram dois casos bem diferentes exibidos com o mesmo
 * chip âmbar — foi essa confusão que originou a coluna.
 */
export const aguardandoDe = (r) => {
  const nada = { texto: '—', papeis: [], tipo: 'nenhum' };
  if (!r) return nada;

  switch (r.STATUS) {
    case 'Certificado':
    case 'Cancelado':
      // Fim de linha: ninguém deve nada.
      return nada;

    case 'Rascunho':
      // Etapa 1 nem foi finalizada — está com quem preenche, não com quem valida.
      return { texto: 'Preenchimento (rascunho)', papeis: [], tipo: 'trabalho' };

    case 'Em diagnóstico': {
      const f = faltantes(r.VALID_DIAG_GERENTE_NOME, r.VALID_DIAG_CONSULTOR_NOME);
      if (f.length === 0) return { texto: 'Iniciar Etapa 2', papeis: [], tipo: 'trabalho' };
      return { texto: `Validação Etapa 1: ${f.join(' + ')}`, papeis: f, tipo: 'validacao' };
    }

    case 'Em execução':
      // Etapa 2 aberta em rascunho: está com a Manutenção, não com os validadores.
      return { texto: 'Conclusão da Etapa 2', papeis: [], tipo: 'trabalho' };

    case 'Certificado com Ressalvas': {
      // Certificado PROVISÓRIO: o status já quer dizer "aguardando as duas
      // assinaturas finais" — a coluna diz QUAIS ainda faltam.
      const f = faltantes(r.VALID_GERENTE_NOME, r.VALID_CONSULTOR_NOME);
      if (f.length === 0) return nada; // não deve ocorrer (o back promove a 'Certificado')
      return { texto: `Validação Etapa 2: ${f.join(' + ')}`, papeis: f, tipo: 'validacao' };
    }

    default:
      return nada;
  }
};

/**
 * Data do último ato de negócio do relatório — o marco a partir do qual ele
 * está parado.
 *
 * ⚠️⚠️ NÃO usar DATA_MODIFICACAO. Ela é `on update CURRENT_TIMESTAMP` e carimba
 * qualquer UPDATE, inclusive administrativo: medido em 17/08/2026, 11 relatórios
 * carregavam o mesmo '2026-08-07 09:36' (a migração das fotos para o SharePoint)
 * e outros 4 estavam "modificados hoje" sem ninguém os ter tocado. É o mesmo modo
 * de falha do DATA_MODIFICACAO da dtbConfere, já registrado no CLAUDE.md.
 *
 * Por isso o marco é a última ASSINATURA (ato humano, datado, que não se
 * reescreve) e, na ausência de qualquer uma, a criação do relatório. A diferença
 * não é cosmética: o #26 da VEL aparecia como "10 dias" e estava parado há 39.
 */
export const marcoUltimoAto = (r) => {
  if (!r) return null;
  const datas = [
    r.VALID_GERENTE_DATA, r.VALID_CONSULTOR_DATA,
    r.VALID_DIAG_GERENTE_DATA, r.VALID_DIAG_CONSULTOR_DATA,
  ].filter(Boolean).map((d) => new Date(d)).filter((d) => !isNaN(d));

  if (datas.length) return new Date(Math.max(...datas));
  const criacao = r.DATA_CRIACAO ? new Date(r.DATA_CRIACAO) : null;
  return criacao && !isNaN(criacao) ? criacao : null;
};

/**
 * Dias corridos desde o último ato de negócio. `null` quando não há marco algum
 * (relatório sem data — a coluna mostra "—" em vez de inventar zero).
 *
 * Dias CORRIDOS, não úteis: o que se quer medir é o tempo que o equipamento
 * passou sem tratamento, e ele não faz pausa no fim de semana.
 */
export const diasParado = (r, hoje = new Date()) => {
  const marco = marcoUltimoAto(r);
  if (!marco) return null;
  // Zera a hora dos dois lados: interessa a distância em dias de calendário, não
  // o acaso de o diagnóstico ter sido assinado de manhã ou à noite.
  const dia = (d) => Date.UTC(d.getFullYear(), d.getMonth(), d.getDate());
  return Math.max(0, Math.round((dia(hoje) - dia(marco)) / 86400000));
};

/** Relatório ainda em andamento (o oposto de arquivo). */
export const emAndamento = (r) => !['Certificado', 'Cancelado'].includes(r?.STATUS);

/**
 * Ordena a fila pelo mais parado no topo. Sem marco vai para o fim (não dá para
 * dizer que está parado há muito o que não se sabe quando começou).
 */
export const ordenarPorParado = (lista) => lista.slice().sort((a, b) => {
  const da = diasParado(a), db = diasParado(b);
  if (da === null && db === null) return 0;
  if (da === null) return 1;
  if (db === null) return -1;
  return db - da || b.ID - a.ID;
});
